import { SESSION_MAX_AGE_EXCEEDED_CODE, SESSION_MAX_AGE_MESSAGE } from '../constants/session-lifecycle.config';

/** Generic fallback shown for any forced sign-out (idle timeout, heartbeat
 *  auth-failure) whose cause is NOT the PR-B session_max_age_exceeded code —
 *  either because that code is genuinely absent (older/different failure) or
 *  because PR-B hasn't deployed yet. Deliberately distinct wording from
 *  project-registration's SESSION_EXPIRED_MESSAGE (helpers/auth.helpers.ts)
 *  since that one is written for "you were mid-form," not a clean idle/heartbeat
 *  sign-out with no assumed in-progress form. */
export const GENERIC_SESSION_EXPIRED_MESSAGE = 'Your session has ended. Please sign in again.';

/**
 * Reads a `session_max_age_exceeded`-shaped error code defensively. PR-B (the
 * backend companion PR, lp-vx feat/session-absolute-cap) is not merged as of
 * this PR — the code may not exist yet, may never ship in exactly this
 * shape, and even once it exists, the ResponseInterceptor here only unwraps
 * SUCCESS responses (see response.interceptor.ts): error bodies are never
 * unwrapped, so a raw HttpErrorResponse's payload lives under `err.error`,
 * not `err`. Check every plausible nesting so this degrades gracefully
 * whichever shape (or absence) shows up:
 *   - err.error.code            (flat error body: { code, message })
 *   - err.error.error.code      (nested error envelope, matches this app's
 *                                 existing `err.error?.error?.code` reads —
 *                                 see pif.helpers.ts)
 *   - err.error.data.code       (in case PR-B follows the {success,data} shape
 *                                 even on an error status)
 *   - err.code                  (in case some caller already unwrapped it)
 */
export function isSessionMaxAgeExceeded(err: unknown): boolean {
  const e = err as any;
  const code =
    e?.error?.code ??
    e?.error?.error?.code ??
    e?.error?.data?.code ??
    e?.code;
  return code === SESSION_MAX_AGE_EXCEEDED_CODE;
}

/** Precise messaging when PR-B's code is present; the existing generic
 *  dead-session message otherwise. Never throws on a malformed/absent err. */
export function messageForSessionEnd(err?: unknown): string {
  try {
    if (err !== undefined && isSessionMaxAgeExceeded(err)) return SESSION_MAX_AGE_MESSAGE;
  } catch {
    /* fall through to generic message — a bad error shape must never break sign-out */
  }
  return GENERIC_SESSION_EXPIRED_MESSAGE;
}
